import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem,
  Tooltip, alpha, Avatar, Divider, Skeleton, Tabs, Tab
} from '@mui/material';
import {
  Search, Payment as PaymentIcon, Close, Delete, Business, People,
  AccountBalance, TrendingUp, Receipt
} from '@mui/icons-material';
import { paymentsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useTableSort } from '../../components/useTableSort';

const fmt = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

const methodLabels = {
  check: 'Check', ach: 'ACH', wire: 'Wire', cash: 'Cash',
  credit_card: 'Credit Card', other: 'Other'
};

const statusColors = {
  completed: { bg: alpha('#10b981', 0.1), color: '#059669' },
  pending: { bg: alpha('#f59e0b', 0.1), color: '#d97706' },
  failed: { bg: alpha('#ef4444', 0.1), color: '#dc2626' },
  refunded: { bg: alpha('#8b5cf6', 0.1), color: '#7c3aed' }
};

const Payments = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([paymentsAPI.getAll({ limit: 1000 }), paymentsAPI.getSummary()]);
      setRows(Array.isArray(p.data) ? p.data : p.data?.data || []);
      setSummary(s.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Payments | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);

  const searchFiltered = rows.filter(r => {
    if (typeFilter !== 'all' && r.invoiceType !== typeFilter) return false;
    const searchStr = [r.entityName || '', r.invoiceNumber || '', r.paymentId || '', r.referenceNumber || ''].join(' ').toLowerCase();
    return searchStr.includes(search.toLowerCase());
  });

  const payColumns = useMemo(() => [
    { id: 'paymentId', label: 'Payment ID', getValue: r => r.paymentId || '' },
    { id: 'paymentDate', label: 'Date', getValue: r => r.paymentDate || '', getDisplay: r => r.paymentDate ? new Date(r.paymentDate).toLocaleDateString() : '—' },
    { id: 'invoiceType', label: 'Type', getValue: r => r.invoiceType === 'provider' ? 'Provider' : 'Contractor' },
    { id: 'invoiceNumber', label: 'Invoice #', getValue: r => r.invoiceNumber || '' },
    { id: 'entityName', label: 'Provider / Contractor', getValue: r => r.entityName || '' },
    { id: 'amount', label: 'Amount', getValue: r => parseFloat(r.amount) || 0, align: 'right' },
    { id: 'paymentMethod', label: 'Method', getValue: r => r.paymentMethod || '' },
    { id: 'referenceNumber', label: 'Reference', getValue: r => r.referenceNumber || '' },
    { id: 'status', label: 'Status', getValue: r => r.status || 'completed' },
  ], []);

  const { sortedData: filtered, renderHeader: renderPayHeader, renderFilterPopover: renderPayFilterPopover, renderClearFilters: renderPayClearFilters } = useTableSort(payColumns, searchFiltered, { defaultSort: 'paymentDate', defaultDir: 'desc' });

  const handleDelete = async () => {
    try {
      await paymentsAPI.delete(deleteId);
      setDeleteId(null); load();
      toast.success('Payment reversed successfully');
    } catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
  };

  const sm = summary?.thisMonth || { provider: { total: 0, count: 0 }, contractor: { total: 0, count: 0 }, grand: { total: 0, count: 0 } };
  const sa = summary?.allTime || { provider: { total: 0, count: 0 }, contractor: { total: 0, count: 0 }, grand: { total: 0, count: 0 } };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Payments</Typography>
          <Typography variant="body2" color="text.secondary">Track and manage all payment transactions</Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'This Month', value: fmt(sm.grand.total), sub: `${sm.grand.count} payments`, color: '#3b82f6', icon: <TrendingUp sx={{ fontSize: 20 }} /> },
          { label: 'Provider Payments', value: fmt(sm.provider.total), sub: `${sm.provider.count} this month`, color: '#10b981', icon: <Business sx={{ fontSize: 20 }} /> },
          { label: 'Contractor Payments', value: fmt(sm.contractor.total), sub: `${sm.contractor.count} this month`, color: '#8b5cf6', icon: <People sx={{ fontSize: 20 }} /> },
          { label: 'All-Time Total', value: fmt(sa.grand.total), sub: `${sa.grand.count} total payments`, color: '#f59e0b', icon: <AccountBalance sx={{ fontSize: 20 }} /> },
        ].map((s, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card sx={{ borderLeft: `4px solid ${s.color}` }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: s.color, mt: 0.5 }}>{s.value}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.3 }}>{s.sub}</Typography>
                  </Box>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(s.color, 0.1), color: s.color, borderRadius: 2 }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search payments..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment> }}
              sx={{ width: 280 }} />
            <Tabs value={typeFilter} onChange={(_, v) => { setTypeFilter(v); setPage(0); }}
              sx={{ '& .MuiTab-root': { textTransform: 'capitalize', fontWeight: 600, fontSize: '0.8rem', minHeight: 36, py: 0 },
                '& .MuiTabs-indicator': { bgcolor: '#3b82f6' } }}>
              <Tab value="all" label="All" />
              <Tab value="provider" label="Provider" />
              <Tab value="contractor" label="Contractor" />
            </Tabs>
            {renderPayClearFilters()}
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  {renderPayHeader(<TableCell align="right">Actions</TableCell>)}
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 6, textAlign: 'center' }}>
                          <PaymentIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No payments found</Typography>
                          <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', mt: 0.5 }}>
                            Payments are recorded from the Invoices page when you click "Record Payment"
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.slice(page * rpp, page * rpp + rpp).map(r => {
                        const sc = statusColors[r.status] || statusColors.completed;
                        const isProvider = r.invoiceType === 'provider';
                        return (
                          <TableRow key={r._id} hover sx={{ '&:hover': { bgcolor: alpha('#3b82f6', 0.03) } }}>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#10b981', fontFamily: 'monospace' }}>{r.paymentId}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.82rem' }}>
                                {r.paymentDate ? new Date(r.paymentDate).toLocaleDateString() : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={isProvider ? <Business sx={{ fontSize: 14 }} /> : <People sx={{ fontSize: 14 }} />}
                                label={isProvider ? 'Provider' : 'Contractor'} size="small"
                                sx={{ fontWeight: 700, height: 24, bgcolor: isProvider ? alpha('#3b82f6', 0.08) : alpha('#8b5cf6', 0.08), color: isProvider ? '#2563eb' : '#7c3aed', '& .MuiChip-icon': { color: 'inherit' } }} />
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#3b82f6', fontFamily: 'monospace' }}>{r.invoiceNumber || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.entityName || '—'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#10b981' }}>{fmt(r.amount)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={methodLabels[r.paymentMethod] || r.paymentMethod} size="small"
                                sx={{ fontWeight: 600, height: 24, bgcolor: alpha('#64748b', 0.08), color: '#475569' }} />
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontFamily: 'monospace' }}>{r.referenceNumber || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={r.status || 'completed'} size="small" sx={{ fontWeight: 700, height: 24, bgcolor: sc.bg, color: sc.color, textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Reverse Payment">
                                <IconButton size="small" onClick={() => setDeleteId(r._id)} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                  <Delete sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]} />
            </>
          )}
        </CardContent>
      </Card>
      {renderPayFilterPopover()}

      {/* Delete/Reverse Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Reverse Payment?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>
            This will reverse the payment and update the invoice balance. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Reverse</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payments;
